import { Flex, Spin } from 'antd';
import { Navigate, Outlet } from 'react-router-dom';
import { useListMembershipsQuery } from '@/entities/user';
import { useAppSelector } from '@/app/store/hooks';
import { ROUTES } from '@/shared/config/routes';

export interface RequireMembershipProps {
  /** Restricts to these StoreRole values; omit to allow any approved role. */
  roles?: readonly string[];
}

/** Gate for `/admin` — checks the current store's membership status/role, not just that the user
 *  is logged in (see `RequireAuth` for that). No membership at all sends the user to
 *  `/create-store` (the only way to get one); an unapproved membership or a role outside `roles`
 *  goes to `/no-access`. */
export function RequireMembership({ roles }: RequireMembershipProps) {
  const currentStoreId = useAppSelector((state) => state.store.currentStoreId);
  const { data: memberships, isLoading } = useListMembershipsQuery();

  if (isLoading || !memberships) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
        <Spin size="large" />
      </Flex>
    );
  }

  const membership = currentStoreId
    ? memberships.find((m) => m.storeId === currentStoreId)
    : undefined;

  if (!membership) {
    return <Navigate to={ROUTES.createStore} replace />;
  }
  if (membership.status !== 'APPROVED' || (roles && !roles.includes(membership.role))) {
    return <Navigate to={ROUTES.noAccess} replace />;
  }

  return <Outlet />;
}
